import Commons from '../../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * Step00_Wait 대시보드 스키마
 * - 워크플로 단계, 최근 활동, 진행 통계, 활동 로그 API 매핑
 */

/** 단계 상태: 완료 | 진행중 | 대기 */
export const WAIT_STEP_STATUS = {
  completed: 'completed',
  inProgress: 'inProgress',
  pending: 'pending',
};

/**
 * STEP_SEQ(TB_SF_RISK_PROGRESS)별 화면 라우트·부제를 한 객체에서 정의
 * 추후 단계 추가 시 테이블 STEP_SEQ와 맞춰 항목만 추가/수정하면 됨.
 * 현재: 1 안전보건정보, 2 위험성평가 실시, 3 개선보고서 — 4~7은 예약
 */
const WAIT_WORKFLOW_STEP_CONFIG_BY_SEQ = {
  1: { routeStepId: 'safetyInfo', subtitle: '설비·물질·작업 정보' },
  2: { routeStepId: 'riskAssessment', subtitle: '유해위험요인 파악 및 평가' },
  3: { routeStepId: 'improvement', subtitle: '감소대책 수립 및 이행' },
  4: { routeStepId: '', subtitle: '' },
  5: { routeStepId: '', subtitle: '' },
  6: { routeStepId: '', subtitle: '' },
  7: { routeStepId: '', subtitle: '' },
};

/** 위험성평가 실시 저장(SaveRisCustom02U1) 시 StepSeq — WAIT_WORKFLOW_STEP_CONFIG_BY_SEQ 키와 동일 */
export const WAIT_STEP_SEQ_RISK_ASSESSMENT = 2;
/** 저장 후 다음(개선보고서 단계 진행) 시 StepSeq */
export const WAIT_STEP_SEQ_IMPROVEMENT = 3;

export const Wait_Schema = {
  fields: {
    assessmentTitle: { initial: '', label: '위험성평가명' },
    overallProgressPercent: { initial: 0, label: '전체 진행률(%)' },
    riskIdentificationDone: { initial: 0, label: '파악 완료 수' },
    riskIdentificationTotal: { initial: 0, label: '전체 공정 수' },
    processTotal: { initial: 0, label: '전체 공정' },
    processIdentified: { initial: 0, label: '파악 완료' },
    processIdentifying: { initial: 0, label: '파악 중' },
    processNotStarted: { initial: 0, label: '미착수' },
  },

  mapper: {
    /**
     * GetRisCustom00R1 — [FromBody] GetRisCustom00R1Request
     * RsCd, SearchText(AES). 대시보드는 SearchText 빈 문자열.
     */
    getRisCustom00R1: {
      RsCd: (_, context) => {
        const n = Number(context.rsCd);
        return Number.isFinite(n) && n > 0 ? n : 0;
      },
      SearchText: (_, context) =>
        Commons.EncryptAES(String(context.searchText ?? '').trim()),
    },
    /**
     * GetRisCustom00R2 — 동일 요청 DTO (RsCd, SearchText AES)
     */
    getRisCustom00R2: {
      RsCd: (_, context) => {
        const n = Number(context.rsCd);
        return Number.isFinite(n) && n > 0 ? n : 0;
      },
      SearchText: (_, context) =>
        Commons.EncryptAES(String(context.searchText ?? '').trim()),
    },
  },
};

/** GetRisCustom00R1Result (Data[0]) */
const LooksLikeGetRisCustom00R1Bundle = (item) => {
  if (!item || typeof item !== 'object') return false;
  return (
    Array.isArray(item.ProgressList) ||
    Array.isArray(item.progressList) ||
    Array.isArray(item.ActivityLogList) ||
    Array.isArray(item.activityLogList) ||
    item.ProgressRateTxt != null ||
    item.progressRateTxt != null
  );
};

/** 대시보드 묶음 DTO 여부(레거시) */
const LooksLikeWaitDashboardAggregate = (item) => {
  if (!item || typeof item !== 'object') return false;
  return (
    item.Steps != null ||
    item.steps != null ||
    item.Progress != null ||
    item.progress != null ||
    Array.isArray(item.Activities) ||
    Array.isArray(item.activities) ||
    item.AssessmentTitle != null ||
    item.assessmentTitle != null
  );
};

/** 활동 로그 행 단건 여부 */
const LooksLikeActivityLogRow = (item) => {
  if (!item || typeof item !== 'object') return false;
  const hasMsg =
    item.LogMsg != null ||
    item.logMsg != null ||
    item.Text != null ||
    item.text != null ||
    item.Message != null ||
    item.message != null;
  return hasMsg && !LooksLikeWaitDashboardAggregate(item);
};

const MapDashboardActivityItemToCard = (a) => ({
  activityType: a.ActivityType ?? a.activityType ?? 'check',
  categoryKey:
    a.CategoryKey ??
    a.categoryKey ??
    InferCategoryKeyFromLogType(
      a.LogType ?? a.logType ?? a.ActivityType ?? a.activityType
    ),
  text:
    a.Text ?? a.text ?? a.Message ?? a.message ?? a.LogMsg ?? a.logMsg ?? '',
  at:
    a.At ??
    a.at ??
    a.Date ??
    a.date ??
    a.LogTimeDisplay ??
    a.logTimeDisplay ??
    '',
  by:
    a.By ??
    a.by ??
    a.UserName ??
    a.userName ??
    a.CreateNm ??
    a.createNm ??
    a.CreateId ??
    a.createId ??
    '',
});

/** GetRisCustom00R1 ProgressRateTxt (예: "57%") → 0~100 */
const ParseProgressRatePercent = (txt) => {
  const s = String(txt ?? '').replace(/\s/g, '');
  const num = parseInt(String(s).replace(/%/g, ''), 10);
  if (!Number.isFinite(num)) return 0;
  return Math.min(100, Math.max(0, num));
};

/** TB_SF_RISK_PROGRESS.STEP_STATUS → WAIT_STEP_STATUS */
const MapStepStatusFromProgressRow = (stepStatus) => {
  const s = String(stepStatus ?? '').trim();
  if (!s) return WAIT_STEP_STATUS.pending;
  const u = s.toUpperCase();
  if (u === '완료' || u === 'COMPLETED' || u === 'COMPLETE' || s === '완료')
    return WAIT_STEP_STATUS.completed;
  if (
    s.includes('진행') ||
    u === 'INPROGRESS' ||
    u === 'IN_PROGRESS' ||
    u === 'PROGRESS'
  )
    return WAIT_STEP_STATUS.inProgress;
  return WAIT_STEP_STATUS.pending;
};

const StrTrim = (v) => String(v ?? '').trim();

/**
 * 공정 1건의 세부행(details) 기준 위험성 파악 상태.
 * RIS_BASIC_01 `RiskAssessment3LevelModal` 완료/진행중 판정과 동일:
 * - 완료: 개선완료일(gaesunComDate) + 감소대책(reductionMeasures) 비어 있지 않음
 * - 진행중: 완료 아님 + (위험발생상황 및 결과) categoryComment + 현재안전보건조치 todaySafety) 시작됐거나 일부 행만 완료
 * - 미착수: 세부행 없음 또는 위 시작 조건을 만족하는 행 없음
 *
 * @returns {'completed'|'inProgress'|'notStarted'}
 */
export const ClassifyProcessIdentificationStatus = (details) => {
  const rows = Array.isArray(details) ? details : [];
  if (rows.length === 0) return 'notStarted';

  const rowStarted = (r) =>
    StrTrim(r.categoryComment) && StrTrim(r.todaySafety);
  const rowComplete = (r) =>
    StrTrim(r.gaesunComDate) && StrTrim(r.reductionMeasures);

  if (rows.every(rowComplete)) return 'completed';
  if (rows.some(rowStarted) || rows.some(rowComplete)) return 'inProgress';
  return 'notStarted';
};

/**
 * MergeSafetyProcessListWithRiskDetails 결과 배열 → 전체공정 / 파악완료·파악중·미착수 / 진행률 분자·분모
 */
export const ComputeIdentificationStatsFromMergedProcesses = (
  mergedProcesses
) => {
  const list = Array.isArray(mergedProcesses) ? mergedProcesses : [];
  let completed = 0;
  let inProgress = 0;
  let notStarted = 0;
  for (const p of list) {
    const st = ClassifyProcessIdentificationStatus(p.details);
    if (st === 'completed') completed += 1;
    else if (st === 'inProgress') inProgress += 1;
    else notStarted += 1;
  }
  const total = list.length;
  return {
    processTotal: total,
    processIdentified: completed,
    processIdentifying: inProgress,
    processNotStarted: notStarted,
    riskIdentificationDone: completed,
    riskIdentificationTotal: total,
  };
};

/** RiskAssessment_ColumnDefs와 동일 — 평가방식 문자열 정규화 */
export const NormalizeRsMethod = (v) =>
  String(v ?? '')
    .replace(/[·ㆍ]/g, '')
    .replace(/\s+/g, '')
    .trim();

/** 빈도·강도법 여부 */
export const IsFrequencyIntensityMethod = (v) =>
  NormalizeRsMethod(v) === NormalizeRsMethod('빈도·강도법');

/**
 * 부서별 요약 테이블 종류: 빈도·강도법 6구간(매우낮음~매우높음) | 3단계 판단법(상·중·하)
 * 평가방식 미지정 시 빈도·강도 6구간으로 간주
 */
export const ResolveWaitDepartmentRiskTableKind = (rsMethod) => {
  if (IsFrequencyIntensityMethod(rsMethod)) return 'fi';
  if (!NormalizeRsMethod(rsMethod)) return 'fi';
  return 'threeLevel';
};

/** 세부행 → 빈도·강도 6구간(1–2 매우낮음 … 16–20 매우높음, Regulation 5×4 구간과 동일) */
const DetailRowToFiBucket = (row) => {
  if (!row || typeof row !== 'object') return null;
  const scoreRaw = row.todayCurrentRisk ?? row.TodayCurrentRisk;
  const n = Number(String(scoreRaw ?? '').trim());
  if (Number.isFinite(n) && n > 0) {
    if (n >= 16) return 'veryHigh';
    if (n >= 11) return 'high';
    if (n >= 9) return 'slightlyHigh';
    if (n >= 6) return 'normal';
    if (n >= 3) return 'low';
    return 'veryLow';
  }
  const j = String(row.riskLevelJudgment ?? row.RiskLevelJudgment ?? '')
    .replace(/\s+/g, '')
    .trim();
  if (j === '매우높음') return 'veryHigh';
  if (j === '높음') return 'high';
  if (j === '약간높음') return 'slightlyHigh';
  if (j === '보통') return 'normal';
  if (j === '낮음') return 'low';
  if (j === '매우낮음') return 'veryLow';
  if (j === '중간') return 'normal';
  if (j === '상') return 'high';
  if (j === '중') return 'normal';
  if (j === '하') return 'veryLow';
  return null;
};

/** 세부행 → 3단계 판단(위험성수준) 상·중·하 */
const DetailRowToThreeLevelBucket = (row) => {
  if (!row || typeof row !== 'object') return null;
  const j = String(row.riskLevelJudgment ?? row.RiskLevelJudgment ?? '').trim();
  if (j === '상') return 'sang';
  if (j === '중') return 'jung';
  if (j === '하') return 'ha';
  return null;
};

const EmptyProgressDashboard = (options = {}) => ({
  totalDiscoveries: 0,
  improvementActions: 0,
  highRiskCount: 0,
  improvementRatePercent: 0,
  improvementRateDeltaPercent: null,
  departmentChart: [],
  departmentRiskTable: {
    kind: ResolveWaitDepartmentRiskTableKind(options.rsMethod),
    rows: [],
  },
});

const ParsePercentStringToNumber = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return 0;
  const n = Number(s.replace(/%/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

const MapDepStatListToDepartmentChart = (depStatList) => {
  const rows = Array.isArray(depStatList) ? depStatList : [];
  return rows.map((r) => ({
    name: String(r.DepNm ?? r.depNm ?? '').trim() || '미지정',
    discoveries: Number(r.TotalCount ?? r.totalCount ?? 0) || 0,
    improvements: Number(r.ImprovedCount ?? r.improvedCount ?? 0) || 0,
  }));
};

const BuildDepRiskCountMap = (depRiskLevelList) => {
  const rows = Array.isArray(depRiskLevelList) ? depRiskLevelList : [];
  const byDep = new Map();
  for (const r of rows) {
    const depNm = String(r.DepNm ?? r.depNm ?? '').trim() || '미지정';
    const levelNm = String(r.RiskLevelNm ?? r.riskLevelNm ?? '')
      .replace(/\s+/g, '')
      .trim();
    const cnt = Number(r.RiskLevelCount ?? r.riskLevelCount ?? 0) || 0;
    if (!byDep.has(depNm)) byDep.set(depNm, {});
    const bucket = byDep.get(depNm);
    bucket[levelNm] = (bucket[levelNm] || 0) + cnt;
  }
  return byDep;
};

const MapDepRiskLevelListToDepartmentRiskTable = (
  depRiskLevelList,
  rsMethod
) => {
  const tableKind = ResolveWaitDepartmentRiskTableKind(rsMethod);
  const byDep = BuildDepRiskCountMap(depRiskLevelList);
  const depNames = Array.from(byDep.keys()).sort((a, b) =>
    String(a).localeCompare(String(b), 'ko')
  );
  if (tableKind === 'threeLevel') {
    return {
      kind: 'threeLevel',
      rows: depNames.map((depNm) => {
        const c = byDep.get(depNm) || {};
        return {
          depNm,
          sang: Number(c.상 || 0),
          jung: Number(c.중 || 0),
          ha: Number(c.하 || 0),
        };
      }),
    };
  }
  return {
    kind: 'fi',
    rows: depNames.map((depNm) => {
      const c = byDep.get(depNm) || {};
      return {
        depNm,
        veryLow: Number(c.매우낮음 || c['매우 낮음'] || 0),
        low: Number(c.낮음 || 0),
        normal: Number(c.보통 || c.중간 || 0),
        slightlyHigh: Number(c.약간높음 || c['약간 높음'] || 0),
        high: Number(c.높음 || 0),
        veryHigh: Number(c.매우높음 || c['매우 높음'] || 0),
      };
    }),
  };
};

/**
 * 병합 공정+세부행 → Wait「진행 현황」대시보드(부서별 차트·요약·KPI)
 * @param {object} [options]
 * @param {string} [options.rsMethod] 평가방식 — 부서별 요약 열(5단계 vs 상·중·하)
 */
export const ComputeWaitProgressDashboardFromMergedProcesses = (
  mergedProcesses,
  options = {}
) => {
  const { rsMethod } = options;
  const tableKind = ResolveWaitDepartmentRiskTableKind(rsMethod);

  const list = Array.isArray(mergedProcesses) ? mergedProcesses : [];
  if (list.length === 0) return EmptyProgressDashboard(options);

  const byDept = new Map();

  const EnsureDept = (depNm) => {
    const key = depNm || '미지정';
    if (!byDept.has(key)) {
      byDept.set(key, {
        depNm: key,
        discoveries: 0,
        improvements: 0,
        veryLow: 0,
        low: 0,
        normal: 0,
        slightlyHigh: 0,
        high: 0,
        veryHigh: 0,
        sang: 0,
        jung: 0,
        ha: 0,
      });
    }
    return byDept.get(key);
  };

  let totalDiscoveries = 0;
  let improvementActions = 0;
  let highRiskCount = 0;

  for (const proc of list) {
    const depNm = StrTrim(proc.depNm) || '미지정';
    const agg = EnsureDept(depNm);
    const details = Array.isArray(proc.details) ? proc.details : [];
    for (const d of details) {
      totalDiscoveries += 1;
      agg.discoveries += 1;
      if (StrTrim(d.gaesunComDate)) {
        improvementActions += 1;
        agg.improvements += 1;
      }
      if (tableKind === 'fi') {
        const bucket = DetailRowToFiBucket(d);
        if (bucket === 'high' || bucket === 'veryHigh') {
          highRiskCount += 1;
        }
        if (bucket === 'veryLow') agg.veryLow += 1;
        else if (bucket === 'low') agg.low += 1;
        else if (bucket === 'normal') agg.normal += 1;
        else if (bucket === 'slightlyHigh') agg.slightlyHigh += 1;
        else if (bucket === 'high') agg.high += 1;
        else if (bucket === 'veryHigh') agg.veryHigh += 1;
      } else {
        const t = DetailRowToThreeLevelBucket(d);
        if (t === 'sang') {
          highRiskCount += 1;
          agg.sang += 1;
        } else if (t === 'jung') agg.jung += 1;
        else if (t === 'ha') agg.ha += 1;
      }
    }
  }

  const improvementRatePercent =
    totalDiscoveries > 0
      ? Math.round((improvementActions / totalDiscoveries) * 1000) / 10
      : 0;

  const rows = Array.from(byDept.values()).sort((a, b) =>
    String(a.depNm).localeCompare(String(b.depNm), 'ko')
  );

  const departmentChart = rows.map((r) => ({
    name: r.depNm,
    discoveries: r.discoveries,
    improvements: r.improvements,
  }));

  const departmentRiskTable =
    tableKind === 'fi'
      ? {
          kind: 'fi',
          rows: rows.map((r) => ({
            depNm: r.depNm,
            veryLow: r.veryLow,
            low: r.low,
            normal: r.normal,
            slightlyHigh: r.slightlyHigh,
            high: r.high,
            veryHigh: r.veryHigh,
          })),
        }
      : {
          kind: 'threeLevel',
          rows: rows.map((r) => ({
            depNm: r.depNm,
            sang: r.sang,
            jung: r.jung,
            ha: r.ha,
          })),
        };

  return {
    totalDiscoveries,
    improvementActions,
    highRiskCount,
    improvementRatePercent,
    improvementRateDeltaPercent: null,
    departmentChart,
    departmentRiskTable,
  };
};

/** GetRisCustom00R1.ProgressList[] → 동적 스텝 목록 */
const MapProgressListToStepStatusList = (progressList) => {
  const rows = Array.isArray(progressList) ? [...progressList] : [];
  rows.sort(
    (a, b) =>
      Number(a.StepSeq ?? a.stepSeq ?? 0) - Number(b.StepSeq ?? b.stepSeq ?? 0)
  );
  return rows.map((row, index) => {
    const seq = Number(row.StepSeq ?? row.stepSeq ?? index + 1);
    const cfg = WAIT_WORKFLOW_STEP_CONFIG_BY_SEQ[seq];
    const routeStepId = String(cfg?.routeStepId ?? '').trim();
    const stepId = routeStepId || `step-${seq}`;
    return {
      stepSeq: seq,
      stepId,
      routeStepId,
      title: String(row.StepNm ?? row.stepNm ?? `단계 ${seq}`),
      subtitle: (routeStepId && String(cfg?.subtitle ?? '').trim()) || '',
      status: MapStepStatusFromProgressRow(row.StepStatus ?? row.stepStatus),
      updateDate: row.UpdateDate ?? row.updateDate ?? '',
      currentStepSeq: Number(row.CurrentStepSeq ?? row.currentStepSeq ?? 0),
    };
  });
};

/**
 * GetRisCustom00R1Result 단일 객체 → 대시보드 뷰 모델
 * (진행률·단계·최근 활동만 서버 제공, 공정 통계는 미제공 → 0)
 */
export const MapGetRisCustom00R1BundleToViewModel = (bundle, options = {}) => {
  if (!bundle || typeof bundle !== 'object') return null;
  const progressList = bundle.ProgressList ?? bundle.progressList ?? [];
  const activityLogList =
    bundle.ActivityLogList ?? bundle.activityLogList ?? [];
  const progressRateTxt =
    bundle.ProgressRateTxt ?? bundle.progressRateTxt ?? '';
  const dashboardSummary =
    bundle.DashboardSummary ?? bundle.dashboardSummary ?? null;
  const depStatList = bundle.DepStatList ?? bundle.depStatList ?? [];
  const depRiskLevelList =
    bundle.DepRiskLevelList ?? bundle.depRiskLevelList ?? [];
  const summaryRsMethod =
    dashboardSummary?.RsMethod ??
    dashboardSummary?.rsMethod ??
    options.rsMethod;
  return {
    assessmentTitle: options.titleOverride || '',
    overallProgressPercent: ParseProgressRatePercent(progressRateTxt),
    steps: MapProgressListToStepStatusList(progressList),
    activities: activityLogList.map(MapDashboardActivityItemToCard),
    riskIdentificationDone: Number(
      dashboardSummary?.ImprovedCount ?? dashboardSummary?.improvedCount ?? 0
    ),
    riskIdentificationTotal: Number(
      dashboardSummary?.TotalCount ?? dashboardSummary?.totalCount ?? 0
    ),
    processTotal: 0,
    processIdentified: 0,
    processIdentifying: 0,
    processNotStarted: 0,
    progressDashboard: {
      totalDiscoveries: Number(
        dashboardSummary?.TotalCount ?? dashboardSummary?.totalCount ?? 0
      ),
      improvementActions: Number(
        dashboardSummary?.ImprovedCount ?? dashboardSummary?.improvedCount ?? 0
      ),
      highRiskCount: Number(
        dashboardSummary?.HighRiskCount ?? dashboardSummary?.highRiskCount ?? 0
      ),
      improvementRatePercent: ParsePercentStringToNumber(
        dashboardSummary?.ImproveRateTxt ?? dashboardSummary?.improveRateTxt
      ),
      improvementRateDeltaPercent: null,
      departmentChart: MapDepStatListToDepartmentChart(depStatList),
      departmentRiskTable: MapDepRiskLevelListToDepartmentRiskTable(
        depRiskLevelList,
        summaryRsMethod
      ),
    },
  };
};

/**
 * 서버 응답 → 대시보드 뷰 모델
 * GetRisCustom00R1: Data[0] = ProgressList + ActivityLogList + ProgressRateTxt
 * Data가 활동 로그 배열만 담은 경우 전체를 activities로 사용(레거시)
 */
export const MapWaitDashboardR1ToViewModel = (raw, options = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  const dataArr = Array.isArray(raw?.Data)
    ? raw.Data
    : Array.isArray(raw?.data)
      ? raw.data
      : null;

  let d = raw?.Data?.[0] ?? raw?.data?.[0] ?? raw;
  let activitiesSource =
    d?.Activities ??
    d?.activities ??
    d?.RecentActivities ??
    d?.recentActivities;

  if (Array.isArray(dataArr) && dataArr.length > 0) {
    const head = dataArr[0];
    if (LooksLikeGetRisCustom00R1Bundle(head)) {
      return MapGetRisCustom00R1BundleToViewModel(head, options);
    }
    if (LooksLikeWaitDashboardAggregate(head)) {
      d = head;
      activitiesSource =
        d.Activities ??
        d.activities ??
        d.RecentActivities ??
        d.recentActivities;
    } else if (dataArr.every(LooksLikeActivityLogRow)) {
      activitiesSource = dataArr;
      d = raw;
    }
  }

  const steps = (d?.Steps ?? d?.steps ?? []).length
    ? (d.Steps ?? d.steps)
    : null;
  const progress = d?.Progress ?? d?.progress ?? raw?.Progress ?? raw?.progress;

  return {
    assessmentTitle:
      options.titleOverride ||
      d?.AssessmentTitle ||
      d?.assessmentTitle ||
      raw?.AssessmentTitle ||
      raw?.assessmentTitle ||
      '',
    overallProgressPercent: Number(
      d?.OverallProgressPercent ??
        d?.overallProgressPercent ??
        raw?.OverallProgressPercent ??
        raw?.overallProgressPercent ??
        0
    ),
    steps: steps
      ? steps.map((s) => ({
          stepId: s.StepId ?? s.stepId,
          status: s.Status ?? s.status ?? WAIT_STEP_STATUS.pending,
        }))
      : null,
    activities: Array.isArray(activitiesSource)
      ? activitiesSource.map(MapDashboardActivityItemToCard)
      : null,
    riskIdentificationDone: Number(
      progress?.IdentifiedDone ??
        progress?.identifiedDone ??
        d?.RiskIdentificationDone ??
        0
    ),
    riskIdentificationTotal: Number(
      progress?.IdentifiedTotal ??
        progress?.identifiedTotal ??
        d?.RiskIdentificationTotal ??
        0
    ),
    processTotal: Number(
      progress?.Total ?? progress?.total ?? d?.ProcessTotal ?? 0
    ),
    processIdentified: Number(
      progress?.Identified ?? progress?.identified ?? d?.ProcessIdentified ?? 0
    ),
    processIdentifying: Number(
      progress?.Identifying ??
        progress?.identifying ??
        d?.ProcessIdentifying ??
        0
    ),
    processNotStarted: Number(
      progress?.NotStarted ?? progress?.notStarted ?? d?.ProcessNotStarted ?? 0
    ),
  };
};

/** 전체보기 모달 — 카테고리 뱃지 (LOG_TYPE START/UPDATE → 시작·수정) */
export const ACTIVITY_LOG_CATEGORY_STYLES = {
  start: { label: '시작', bg: '#E3F2FD', color: '#1565C0' },
  update: { label: '수정', bg: '#FFF3E0', color: '#E65100' },
  risk: { label: '위험성 파악', bg: '#E8F5E9', color: '#2E7D32' },
  education: { label: '사전교육', bg: '#E8EAF6', color: '#3949AB' },
  notice: { label: '공지사항', bg: '#F3E5F5', color: '#7B1FA2' },
  info: { label: '정보 입력', bg: '#E0F7FA', color: '#006064' },
  other: { label: '기타', bg: '#F5F5F5', color: '#616161' },
};

/** LOG_TYPE: START→시작, UPDATE→수정 */
export const ACTIVITY_LOG_STATUS_STYLES = {
  start: { label: '시작', color: '#1565C0' },
  completed: { label: '완료', color: '#2E7D32' },
  approved: { label: '승인', color: '#1565C0' },
  modified: { label: '수정', color: '#E65100' },
  registered: { label: '등록', color: '#1565C0' },
  distributed: { label: '배포', color: '#2E7D32' },
};

/** TB_SF_RISK_ACTIVITY_LOG.LOG_TYPE → 카테고리 칼럼 (START→시작, UPDATE→수정) */
export const InferCategoryKeyFromLogType = (logType) => {
  const s = String(logType ?? '').trim();
  const u = s.toUpperCase();
  if (!s) return 'other';
  if (u === 'START') return 'start';
  if (u === 'UPDATE') return 'update';
  if (u.includes('RISK') || s.includes('위험') || u.includes('PAR'))
    return 'risk';
  if (u.includes('EDU') || s.includes('교육') || u.includes('TRAIN'))
    return 'education';
  if (u.includes('NOTICE') || s.includes('공지') || u.includes('ANNOUNCE'))
    return 'notice';
  if (u.includes('INFO') || s.includes('정보') || s.includes('입력'))
    return 'info';
  if (u.includes('UPDATE') || s.includes('업데이트') || s.includes('수정'))
    return 'update';
  return 'other';
};

export const InferStatusKeyFromLogType = (logType) => {
  const s = String(logType ?? '').trim();
  const u = s.toUpperCase();
  if (u === 'START') return 'start';
  if (u === 'UPDATE') return 'modified';
  if (u.includes('APPROV') || s.includes('승인')) return 'approved';
  if (u.includes('REGIST') || s.includes('등록')) return 'registered';
  if (u.includes('DIST') || s.includes('배포')) return 'distributed';
  return 'completed';
};

/** CreateDate(JSON DateTime/문자열) → 일시 컬럼 보조 표시 (LogTimeDisplay 없을 때) */
const FormatActivityLogCreateDateFallback = (v) => {
  if (v == null || v === '') return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('MM.DD HH:mm') : String(v);
};

/**
 * SaveRisBasicResult Data (GetRisCustom00R1ActivityLogItem[]) → 배열
 * (대부분 Data가 List 직렬화 배열, 래퍼 객체일 때만 Items 등 탐색)
 */
export const NormalizeGetRisCustom00R2DataList = (response) => {
  const raw = response ?? {};
  let list = raw.Data ?? raw.data;
  if (Array.isArray(list)) return list;
  if (list != null && typeof list === 'object') {
    const nested =
      list.Items ??
      list.items ??
      list.List ??
      list.list ??
      list.Results ??
      list.results;
    if (Array.isArray(nested)) return nested;
  }
  return [];
};

/**
 * GetRisCustom00R1ActivityLogItem → 모달 행
 * LogId, LogType, LogMsg, CreateId, CreateNm, LogTimeDisplay, CreateDate
 */
export const MapGetRisCustom00R2ItemToRow = (item, index, total) => {
  if (!item || typeof item !== 'object') return null;
  const logType = item.LogType ?? item.logType ?? '';
  const logId = item.LogId ?? item.logId ?? index;
  const createNm = item.CreateNm ?? item.createNm ?? '';
  const createId = item.CreateId ?? item.createId ?? '';
  const logTime = item.LogTimeDisplay ?? item.logTimeDisplay ?? '';
  const createRaw = item.CreateDate ?? item.createDate;
  const at = logTime || FormatActivityLogCreateDateFallback(createRaw) || '';
  return {
    logId,
    no: total > 0 ? total - index : index + 1,
    categoryKey: InferCategoryKeyFromLogType(logType),
    content: item.LogMsg ?? item.logMsg ?? '',
    at,
    performer: createNm || createId || '',
    statusKey: InferStatusKeyFromLogType(logType),
  };
};

/**
 * Ok(SaveRisBasicResult) → 테이블 rows
 */
export const MapGetRisCustom00R2ResponseToRows = (response) => {
  const list = NormalizeGetRisCustom00R2DataList(response);
  const total = list.length;
  return list
    .map((item, index) => MapGetRisCustom00R2ItemToRow(item, index, total))
    .filter(Boolean);
};

/** API 미응답·실패 시 빈 대시보드 (데모 없음) */
export const GetEmptyWaitDashboardViewModel = (titleOverride = '') => ({
  assessmentTitle: titleOverride || '',
  overallProgressPercent: 0,
  steps: null,
  activities: [],
  riskIdentificationDone: 0,
  riskIdentificationTotal: 0,
  processTotal: 0,
  processIdentified: 0,
  processIdentifying: 0,
  processNotStarted: 0,
  progressDashboard: EmptyProgressDashboard(),
});

export default Wait_Schema;
